import { EmptyListContent } from "@jugl-web/ui-components";
import React, { useMemo } from "react";
import { CRMChannel } from "@jugl-web/rest-api";
import { Lottie } from "@jugl-web/utils/utils/Lottie";
import { useTranslations } from "@jugl-web/utils";
import { useClientSupportPageContext } from "../../../../providers/ClientSupportPageProvider/useClientSupportPageContext";
import { useWhatsappAccount } from "../../../../providers/WhatsappAccountProvider/useWhatsappAccount";
import { useInstagramAccount } from "../../../../providers/InstagramAccountProvider/useInstagramAccount";
import { useWebChatBotAccount } from "../../../../providers/WebChatBotAccountProvider/useWebChatBotAccount";
import alertAnimation from "./assets/alert-animation.json";
import { ReactComponent as InstagramIcon } from "./assets/instagram.svg";
import { ReactComponent as WhatsappIcon } from "./assets/whatsapp.svg";
import { ReactComponent as WebChatBotIcon } from "./assets/web-chat-bot.svg";
import { ReactComponent as TwilioIcon } from "./assets/twilio.svg";
import { useManageAccountsDrawer } from "../../../ManageAccountsDrawer/providers/ManageAccountsDrawerContext";

export const SelectChatSection: React.FC = () => {
  const { t } = useTranslations();
  const { selectedChannel } = useClientSupportPageContext();
  const { openManageAccountDrawer } = useManageAccountsDrawer();
  const { whatsappAgent, reconnectWhatsappAccount, isRegisteringAccount } =
    useWhatsappAccount();
  const { instagramAccount, loginInstagramAccount } = useInstagramAccount();
  const { webChatBotAccount } = useWebChatBotAccount();

  const { title, subtitle, isConnected, img, disconnectedButton } =
    useMemo(() => {
      const commonConnectedSubtitle = t({
        id: "client-support-page.you-will-get-notification-on-clients-message",
        defaultMessage:
          "You will get a notification once you receive message from Clients",
      });

      const config = {
        [CRMChannel.whatsapp]: {
          isConnected: whatsappAgent?.phone_number.status === "CONNECTED",
          icons: <WhatsappIcon />,
          connectedTitle: t({
            id: "client-support-page.welcome-to-whatsapp-inbox",
            defaultMessage: "Welcome to WhatsApp Inbox",
          }),
          disconnectedTitle: t({
            id: "client-support-page.whatsapp-account-is-not-connected",
            defaultMessage: "WhatsApp Account is not connected",
          }),
          disconnectedSubtitle: t({
            id: "client-support-page.reconnect-to-continue-using-whatsapp-crm",
            defaultMessage: "Please reconnect to continue using WhatsApp CRM",
          }),
          disconnectedButton: {
            text: t({
              id: "common.reconnect",
              defaultMessage: "Reconnect",
            }),
            onClick: reconnectWhatsappAccount,
            isDisabled: isRegisteringAccount,
          },
        },
        [CRMChannel.instagram]: {
          isConnected: instagramAccount?.status === "connected",
          icons: <InstagramIcon />,
          connectedTitle: t({
            id: "client-support-page.welcome-to-instagram-inbox",
            defaultMessage: "Welcome to Instagram Inbox",
          }),
          disconnectedTitle: t({
            id: "client-support-page.instagram-account-is-not-connected",
            defaultMessage: "Instagram Account is not connected",
          }),
          disconnectedSubtitle: t({
            id: "client-support-page.reconnect-to-continue-using-instagram-crm",
            defaultMessage: "Please reconnect to continue using Instagram CRM",
          }),
          disconnectedButton: {
            text: t({ id: "common.reconnect", defaultMessage: "Reconnect" }),
            onClick: loginInstagramAccount,
          },
        },
        [CRMChannel.chatbot]: {
          isConnected: webChatBotAccount?.enabled,
          icons: <WebChatBotIcon />,
          connectedTitle: t({
            id: "client-support-page.welcome-to-web-chat-inbox",
            defaultMessage: "Welcome to Web Chat Inbox",
          }),
          disconnectedTitle: t({
            id: "client-support-page.web-chat-is-disabled",
            defaultMessage: "Web Chat is disabled",
          }),
          disconnectedSubtitle: t({
            id: "client-support-page.enable-to-continue-using-web-chat-crm",
            defaultMessage: "Please enable it to continue using Web Chat CRM",
          }),
          disconnectedButton: {
            text: t({
              id: "client-support-page.open-settings",
              defaultMessage: "Open Settings",
            }),
            onClick: () =>
              openManageAccountDrawer({ name: "webChatBotAccount" }),
          },
        },
        [CRMChannel.twilio]: {
          isConnected: true,
          icons: <TwilioIcon />,
          connectedTitle: t({
            id: "client-support-page.welcome-to-twilio-inbox",
            defaultMessage: "Welcome to Text (SMS) Inbox",
          }),
          disconnectedTitle: t({
            id: "client-support-page.twilio-is-disabled",
            defaultMessage: "Text (SMS) is disabled",
          }),
          disconnectedSubtitle: t({
            id: "client-support-page.enable-to-continue-using-twilio-crm",
            defaultMessage: "Please enable it to continue using Text (SMS) CRM",
          }),
          disconnectedButton: undefined,
        },
      };

      const channelConfig = config[selectedChannel];
      if (!channelConfig) {
        throw new Error(`Unsupported crm channel type: ${selectedChannel}`);
      }

      return {
        title: channelConfig.isConnected
          ? channelConfig.connectedTitle
          : channelConfig.disconnectedTitle,
        subtitle: channelConfig.isConnected
          ? commonConnectedSubtitle
          : channelConfig.disconnectedSubtitle,
        img: channelConfig.icons,
        isConnected: channelConfig.isConnected,
        disconnectedButton: channelConfig.disconnectedButton,
      };
    }, [
      t,
      whatsappAgent,
      reconnectWhatsappAccount,
      isRegisteringAccount,
      instagramAccount?.status,
      loginInstagramAccount,
      webChatBotAccount?.enabled,
      selectedChannel,
      openManageAccountDrawer,
    ]);

  return (
    <div className="bg-grey-100 h-full w-full">
      <EmptyListContent
        className="w-[450px]"
        type="custom"
        customTitle={title}
        customSubtitle={subtitle}
        customImg={
          <div className="relative flex">
            <div className="drop-shadow-[-12px_12px_15px_rgba(87,68,111,0.16)]">
              {img}
            </div>
            {!isConnected && (
              <div className="absolute -right-2 -bottom-2 flex h-[60px] w-[60px] items-center justify-center rounded-full bg-white">
                <Lottie
                  options={{ animationData: alertAnimation }}
                  height={40}
                  width={40}
                />
              </div>
            )}
          </div>
        }
        customButton={!isConnected ? disconnectedButton : undefined}
      />
    </div>
  );
};
